import { Component, Input, Output, EventEmitter } from '@angular/core';
import { DxPopupModule, DxDataGridModule, DxButtonModule, DxCheckBoxModule } from 'devextreme-angular';
import { CrudService } from '../../services/crud.service';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-duplicate-resolution',
  imports: [DxPopupModule, DxDataGridModule, DxButtonModule, DxCheckBoxModule],
  templateUrl: './duplicate-resolution.component.html',
  styleUrl: './duplicate-resolution.component.css',
  standalone: true
})
export class DuplicateResolutionComponent {
  @Input() visible: boolean = false;
  @Input() duplicates: any[] = [];
  @Output() resolutionSelected = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<void>();

  // Track which groups are selected for removal for each product
  private groupRemovalSelections: Map<string, boolean> = new Map();
  
  private apiUrl : string = environment.baseApiUrl;

  constructor(private crudService: CrudService){}

  isGroupSelectedForRemoval(productId: number, groupId: number): boolean {
    const key = `${productId}_${groupId}`;
    return this.groupRemovalSelections.get(key) || false;
  }

  onGroupSelectionChanged(e: any, productId: number, groupId: number): void {
    const key = `${productId}_${groupId}`;
    this.groupRemovalSelections.set(key, e.value);
  }

  hasValidSelections(): boolean {
    // Check that for each duplicate product, at least one group is NOT selected for removal
    // (meaning the product will remain in at least one group)
    return this.duplicates.every(duplicate => {
      const unselectedGroups = duplicate.promotion_groups.filter((group: any) => 
        !this.isGroupSelectedForRemoval(duplicate.product_id, group.id)
      );
      return unselectedGroups.length > 0;
    });
  }

  async onApply(): Promise<void> {
    const resolution: any = {};
    const deleteOperations: Array<{productId: number, groupId: number, promise: Promise<any>}> = [];
    
    this.duplicates.forEach(duplicate => {
      const groupsToRemoveFrom: number[] = [];
      duplicate.promotion_groups.forEach((group: any) => {
        if (this.isGroupSelectedForRemoval(duplicate.product_id, group.id)) {
          groupsToRemoveFrom.push(group.id);
          
          // Create a promise for each delete operation with metadata
          const deletePromise = firstValueFrom(
            this.crudService.deleteDataWithBody(`${this.apiUrl}promotion-groups-destroy`, {
              product_id: duplicate.product_id, 
              promotion_group_id: group.id
            })
          );
          
          deleteOperations.push({
            productId: duplicate.product_id,
            groupId: group.id,
            promise: deletePromise
          });
        }
      });
      
      if (groupsToRemoveFrom.length > 0) {
        resolution[duplicate.product_id] = groupsToRemoveFrom;
      }
    });

    try {
      // Wait for all delete operations to complete
      const results = await Promise.allSettled(deleteOperations.map(op => op.promise));
      
      const successful: string[] = [];
      const failed: string[] = [];
      
      results.forEach((result, index) => {
        const operation = deleteOperations[index];
        if (result.status === 'fulfilled' && result.value.success === true) {
          successful.push(`Product ${operation.productId} removed from group ${operation.groupId}`);
        } else {
          const errorMsg = result.status === 'rejected' 
            ? result.reason 
            : result.value.message || 'Unknown error';
          failed.push(`Failed to remove product ${operation.productId} from group ${operation.groupId}: ${errorMsg}`);
        }
      });
      
      if (failed.length === 0) {
        console.log('All duplicate removals completed successfully:', successful);
        this.resolutionSelected.emit({ success: true, resolution, successful });
      } else {
        console.warn('Some operations failed:', { successful, failed });
        this.resolutionSelected.emit({ 
          success: false, 
          resolution, 
          successful, 
          failed,
          partialSuccess: successful.length > 0 
        });
      }
      
    } catch (error) {
      console.error('Unexpected error during duplicate resolution:', error);
      this.resolutionSelected.emit({ 
        success: false, 
        error: 'Unexpected error occurred', 
        details: error 
      });
    }
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  ngOnInit(): void {
    // Reset selections when component initializes
    this.groupRemovalSelections.clear();
  }
}
